import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Briefcase, Building2, ChevronRight, MapPin, Monitor, Plus, Tablet, Users2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Club, Court } from "@/types";
import { useAuthStore } from "@/store/auth";
import { SPAIN_COMUNIDADES, SPAIN_PROVINCES_BY_COMUNIDAD } from "@/lib/spainGeo";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, Badge } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { DeviceListEditor } from "@/components/DeviceListEditor";
import { ContactButtons } from "@/components/ContactButtons";

const selectClass =
  "rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

const clubEditSchema = z.object({
  nombre: z.string().min(2, "Mínimo 2 caracteres"),
  direccion: z.string().optional(),
  pais: z.string().optional(),
  comunidadAutonoma: z.string().optional(),
  provincia: z.string().optional(),
  localidad: z.string().optional(),
  descripcion: z.string().optional(),
  imagenUrl: z.string().optional(),
  telefono: z.string().optional(),
  email: z.string().optional(),
});
type ClubEditForm = z.infer<typeof clubEditSchema>;

const courtSchema = z.object({
  name: z.string().min(1, "Nombre obligatorio"),
});
type CourtForm = z.infer<typeof courtSchema>;

// Fila simple de una pista: ya no se despliega en sitio (acordeón), pincharla lleva a su ficha
// propia (CourtDetailPage), que es donde vive ahora toda la edición (ficha, marcador, cámaras...).
function CourtRow({ court, onNavigate }: { court: Court; onNavigate: () => void }) {
  return (
    <button
      onClick={onNavigate}
      className="flex w-full items-center gap-2 rounded-xl border border-ink-100 bg-white px-4 py-3 text-left transition-colors hover:bg-ink-50/50"
    >
      <MapPin className="h-4 w-4 shrink-0 text-brand-600" />
      <span className="flex-1 truncate font-medium text-ink-800">{court.name}</span>
      {!court.active && <Badge tone="red">Inactiva</Badge>}
      <Badge tone={court.cameras?.length ? "green" : "slate"}>{court.cameras?.length ?? 0} cám.</Badge>
      <ChevronRight className="h-4 w-4 shrink-0 text-ink-300" />
    </button>
  );
}

export function ClubDetailPage() {
  const { id } = useParams();
  const clubId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isSuperAdmin = useAuthStore((s) => s.user?.role === "SUPER_ADMIN");
  const [confirmingToggle, setConfirmingToggle] = useState(false);

  const { data: club, isLoading } = useQuery({
    queryKey: ["club", clubId],
    queryFn: async () => (await api.get<Club>(`/clubs/${clubId}`)).data,
  });

  const { data: courts, isLoading: courtsLoading } = useQuery({
    queryKey: ["courts", clubId],
    queryFn: async () => (await api.get<Court[]>(`/courts?clubId=${clubId}`)).data,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ClubEditForm>({ resolver: zodResolver(clubEditSchema) });

  const comunidadSeleccionada = watch("comunidadAutonoma");

  useEffect(() => {
    if (!club) return;
    reset({
      nombre: club.nombre,
      direccion: club.direccion ?? "",
      pais: club.pais ?? "España",
      comunidadAutonoma: club.comunidadAutonoma ?? "",
      provincia: club.provincia ?? "",
      localidad: club.localidad ?? "",
      descripcion: club.descripcion ?? "",
      imagenUrl: club.imagenUrl ?? "",
      telefono: club.telefono ?? "",
      email: club.email ?? "",
    });
  }, [club, reset]);

  const saveClub = useMutation({
    mutationFn: (data: ClubEditForm) =>
      api.put(`/clubs/${clubId}`, {
        nombre: data.nombre,
        direccion: data.direccion || null,
        pais: data.pais || null,
        comunidadAutonoma: data.comunidadAutonoma || null,
        provincia: data.provincia || null,
        localidad: data.localidad || null,
        descripcion: data.descripcion || null,
        imagenUrl: data.imagenUrl || null,
        telefono: data.telefono || null,
        email: data.email || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["club", clubId] });
      queryClient.invalidateQueries({ queryKey: ["clubs"] });
      toast.success("Club actualizado");
    },
    onError: (error: any) => toast.error(error?.response?.data?.error ?? "No se pudo actualizar el club"),
  });

  const toggleActivo = useMutation({
    mutationFn: () => api.put(`/clubs/${clubId}`, { activo: !club!.activo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["club", clubId] });
      queryClient.invalidateQueries({ queryKey: ["clubs"] });
      toast.success(club!.activo ? "Club desactivado" : "Club activado");
    },
    onError: () => toast.error("No se pudo actualizar el club"),
  });

  const {
    register: registerCourt,
    handleSubmit: handleSubmitCourt,
    reset: resetCourt,
    formState: { errors: courtErrors },
  } = useForm<CourtForm>({ resolver: zodResolver(courtSchema) });

  const createCourt = useMutation({
    mutationFn: (data: CourtForm) => api.post("/courts", { ...data, clubId }),
    onSuccess: () => {
      resetCourt();
      queryClient.invalidateQueries({ queryKey: ["courts", clubId] });
      toast.success("Pista añadida");
    },
    onError: () => toast.error("No se pudo crear la pista"),
  });

  if (isLoading || !club) {
    return <p className="text-sm text-ink-400">Cargando club…</p>;
  }

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Clubes", to: "/clubes" }, { label: club.nombre }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
            <Building2 className="h-6 w-6" />
          </span>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-ink-900">
              {club.nombre}
              <ContactButtons telefono={club.telefono} email={club.email} />
            </h1>
            <div className="flex items-center gap-3 text-ink-400">
              <span className="inline-flex items-center gap-1.5">
                <Users2 className="h-3.5 w-3.5" />
                {club._count?.usuarios ?? 0} usuarios
              </span>
              {isSuperAdmin && club.provider && (
                <button
                  type="button"
                  onClick={() => navigate(`/proveedores/${club.provider!.id}`)}
                  className="inline-flex items-center gap-1.5 transition-colors hover:text-brand-600 hover:underline"
                >
                  <Briefcase className="h-3.5 w-3.5" />
                  {club.provider.nombre}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={club.activo ? "green" : "red"}>{club.activo ? "Activo" : "Inactivo"}</Badge>
          <Button className="w-24" onClick={() => setConfirmingToggle(true)}>
            {club.activo ? "Desactivar" : "Activar"}
          </Button>
        </div>
      </div>

      <Card>
        <form onSubmit={handleSubmit((data) => saveClub.mutate(data))} className="flex flex-col gap-4">
          <p className="text-sm font-medium text-ink-600">Ficha del club (lo que verán los clientes en la app)</p>
          <Input id="nombre-club" label="Nombre del club" error={errors.nombre?.message} {...register("nombre")} />
          <Input id="direccion-club" label="Dirección" placeholder="C/ Esquiladores, 237" {...register("direccion")} />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Input id="pais-club" label="País" placeholder="España" {...register("pais")} />
            <div className="flex flex-col gap-1.5">
              <label htmlFor="comunidad-club" className="text-sm font-medium text-ink-600">
                Comunidad autónoma
              </label>
              <select
                id="comunidad-club"
                className={selectClass}
                {...register("comunidadAutonoma", {
                  onChange: () => setValue("provincia", ""),
                })}
              >
                <option value="">Selecciona…</option>
                {SPAIN_COMUNIDADES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="provincia-club" className="text-sm font-medium text-ink-600">
                Provincia
              </label>
              <select id="provincia-club" className={selectClass} disabled={!comunidadSeleccionada} {...register("provincia")}>
                <option value="">Selecciona…</option>
                {(SPAIN_PROVINCES_BY_COMUNIDAD[comunidadSeleccionada ?? ""] ?? []).map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <Input id="localidad-club" label="Localidad" placeholder="Cáceres" {...register("localidad")} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input id="telefono-club" label="Teléfono de contacto" placeholder="600 000 000" {...register("telefono")} />
            <Input id="email-club" label="Email de contacto" type="email" placeholder="club@email.com" {...register("email")} />
          </div>
          <Input id="imagen-club" label="URL de imagen" placeholder="https://…" {...register("imagenUrl")} />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="descripcion-club" className="text-sm font-medium text-ink-600">
              Descripción
            </label>
            <textarea
              id="descripcion-club"
              rows={3}
              className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
              {...register("descripcion")}
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={saveClub.isPending}>
              Guardar cambios
            </Button>
          </div>
        </form>
      </Card>

      <Card className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 rounded-xl border border-ink-100 p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-ink-600">
            <MapPin className="h-4 w-4 text-brand-600" />
            Pistas
          </p>
          {courtsLoading ? (
            <p className="text-sm text-ink-400">Cargando pistas…</p>
          ) : !courts?.length ? (
            <p className="text-sm text-ink-400">Este club todavía no tiene pistas configuradas.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {courts.map((court) => (
                <CourtRow key={court.id} court={court} onNavigate={() => navigate(`/clubes/${clubId}/pistas/${court.id}`)} />
              ))}
            </div>
          )}

          <form onSubmit={handleSubmitCourt((data) => createCourt.mutate(data))} className="flex items-end gap-2">
            <div className="flex-1">
              <Input id="court-name-new" label="Nueva pista" placeholder="Pista 1" error={courtErrors.name?.message} {...registerCourt("name")} />
            </div>
            <Button type="submit" disabled={createCourt.isPending}>
              <Plus className="h-4 w-4" />
              Añadir
            </Button>
          </form>
        </div>

        <DeviceListEditor
          clubId={clubId}
          type="SCREEN_PREVIEW"
          title="Pantallas de preview (partidos en directo del club)"
          icon={Monitor}
          emptyLabel="Este club todavía no tiene pantallas de preview configuradas."
        />
        <DeviceListEditor
          clubId={clubId}
          type="TABLET"
          title="Tablets de árbitro"
          icon={Tablet}
          emptyLabel="Este club todavía no tiene tablets de árbitro registradas."
        />
      </Card>

      {confirmingToggle && (
        <ConfirmDialog
          open={confirmingToggle}
          onOpenChange={setConfirmingToggle}
          title={club.activo ? "Desactivar club" : "Activar club"}
          description={`¿Seguro que quieres ${club.activo ? "desactivar" : "activar"} "${club.nombre}"?`}
          tone="primary"
          confirmLabel={club.activo ? "Desactivar" : "Activar"}
          onConfirm={() => toggleActivo.mutate()}
        />
      )}
    </div>
  );
}
